import { useCallback } from 'react';
import styled from 'styled-components';

import { fileUriToSrc } from '../../../api/files';
import { formatFileSize, getFileInfo } from '../../../utils/fileDisplay';
import { FileUpload } from '../FileUpload';
import { IconDelete, IconDownload } from '../icons';
import { MenuButton } from '../MenuButton';
import type { UploadedFile } from '../FileUpload';

const DOCUMENTS_SECTION = 'documents';
const MAX_DOCUMENTS = 20;

export interface DocumentItem {
  file: UploadedFile;
}

export interface DocumentBlockProps {
  value: DocumentItem[];
  onChange: (documents: DocumentItem[]) => void;
  disabled?: boolean;
  /**
   * Режим внешней страницы (только просмотр): без подсказки про лимит/сохранение,
   * без загрузки файлов и без удаления из списка.
   */
  readOnly?: boolean;
  /** id для секции (для aria-labelledby) */
  id?: string;
  /** Ошибка валидации с сервера (422) для зоны загрузки документов */
  error?: string | null;
}

const Wrapper = styled.div<{ $error?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[2]}px;
  ${({ theme, $error }) =>
    $error
      ? `padding: ${theme.space[1]}px; outline: 2px solid ${theme.colors.error.main}; outline-offset: 2px; border-radius: ${theme.borderRadius}px;`
      : ''}
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[0]}px;
`;

const FileCardRow = styled.li`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: ${({ theme }) => theme.space[6]}px;

  &::before {
    content: '';
    display: block;
    position: absolute;
    left: -${({ theme }) => theme.space[0]}px;
    right: -${({ theme }) => theme.space[0]}px;
    top: -${({ theme }) => theme.space[0]}px;
    bottom: -${({ theme }) => theme.space[0]}px;
    border-radius: ${({ theme }) => theme.borderRadius}px;
    transition: background 200ms ease-in;
    pointer-events: none;
  }

  &:hover::before {
    background: ${({ theme }) => theme.colors.highlight.blue25};
  }
`;

const FileLink = styled.a`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.main};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.borderRadius}px;
  }
`;

const ExtensionBadge = styled.div`
  position: relative;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  width: ${({ theme }) => theme.space[6]}px;
  height: ${({ theme }) => theme.space[6]}px;
  margin-right: ${({ theme }) => theme.space[2]}px;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const ExtensionText = styled.span`
  display: block;
  max-width: 100%;
  padding: 0 ${({ theme }) => theme.space[0]}px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: 600;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const TextColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileTitle = styled.span`
  display: block;
  margin-bottom: ${({ theme }) => theme.space[0]}px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.fontSizes[6]}px;
  font-weight: 500;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FileMeta = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.text.disabled};
`;

const ActionsWrap = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-shrink: 0;
  gap: ${({ theme }) => theme.space[0]}px;
  margin-left: ${({ theme }) => theme.space[2]}px;
  transition: opacity 200ms ease-in;

  ${({ theme }) => theme.device.layoutDesktop} {
    opacity: 0;

    ${FileCardRow}:hover & {
      opacity: 1;
    }
  }
`;

/** Квадратная ссылка «Скачать» визуально как MenuButton (web-client IconButton + download). */
const DownloadIconLink = styled.a`
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  margin: 0;
  width: ${({ theme }) => theme.space[4]}px;
  min-width: ${({ theme }) => theme.space[4]}px;
  height: ${({ theme }) => theme.space[4]}px;
  min-height: ${({ theme }) => theme.space[4]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: 0.2s background-color ease-in-out, 0.2s color ease-in-out;

  & svg {
    color: inherit;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.highlight.gray25};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.main};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.borderRadius}px;
  }
`;

const ErrorText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  color: ${({ theme }) => theme.colors.error.main};
`;

const HelperText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

function buildMetaLine(file: UploadedFile): string {
  const parts: string[] = [];
  if (file.size != null) {
    parts.push(formatFileSize(file.size));
  }
  if (file.description) {
    parts.push(file.description);
  }
  return parts.join(' • ');
}

/** Имя для атрибута download (без путей и управляющих символов). */
function suggestedDownloadName(filename: string): string {
  const tail = filename.replace(/^.*[/\\]/, '');
  const base = Array.from(tail, (ch) => (ch < ' ' ? '_' : ch)).join('');
  return base.length > 0 ? base : 'file';
}

export function DocumentBlock({
  value,
  onChange,
  disabled = false,
  readOnly = false,
  id,
  error = null,
}: DocumentBlockProps) {
  const documents = value;
  const canAdd = documents.length < MAX_DOCUMENTS && !readOnly;

  const handleAdd = useCallback(
    (file: UploadedFile) => {
      onChange([...documents, { file }]);
    },
    [documents, onChange],
  );

  const handleRemove = useCallback(
    (index: number) => {
      onChange(documents.filter((_, i) => i !== index));
    },
    [documents, onChange],
  );

  const documentsError =
    !readOnly && documents.length > MAX_DOCUMENTS
      ? `Максимум ${MAX_DOCUMENTS} документов`
      : null;

  const serverDocumentsError = error?.trim() ? error : null;

  return (
    <Wrapper id={id} $error={Boolean(serverDocumentsError)}>
      {!readOnly && (
        <HelperText>
          Документы (макс. {MAX_DOCUMENTS}). Удаление из списка до сохранения.
        </HelperText>
      )}
      {documentsError && (
        <div role="alert">
          <ErrorText>{documentsError}</ErrorText>
        </div>
      )}
      {serverDocumentsError && !canAdd ? (
        <div role="alert">
          <ErrorText>{serverDocumentsError}</ErrorText>
        </div>
      ) : null}
      {documents.length > 0 && (
        <List>
          {documents.map((item, index) => {
            const label = item.file.filename ?? 'Файл';
            const { name, extension } = getFileInfo(label);
            const displayName = extension ? name : label;
            const extLabel = extension ? `.${extension}` : '—';
            const meta = buildMetaLine(item.file);
            const href = fileUriToSrc(item.file.uri);

            return (
              <FileCardRow key={item.file.id}>
                <FileLink
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={label}
                  aria-label={`Открыть ${label} в новой вкладке`}
                >
                  <ExtensionBadge aria-hidden>
                    <ExtensionText>{extLabel}</ExtensionText>
                  </ExtensionBadge>
                  <TextColumn>
                    <FileTitle>{displayName}</FileTitle>
                    {meta ? <FileMeta>{meta}</FileMeta> : null}
                  </TextColumn>
                </FileLink>
                {readOnly ? (
                  <ActionsWrap>
                    <DownloadIconLink
                      href={href}
                      download={suggestedDownloadName(label)}
                      aria-label={`Скачать ${label}`}
                    >
                      <IconDownload />
                    </DownloadIconLink>
                  </ActionsWrap>
                ) : (
                  <ActionsWrap>
                    <MenuButton
                      aria-label={`Удалить документ ${label}`}
                      disabled={disabled}
                      onClick={() => handleRemove(index)}
                    >
                      <IconDelete />
                    </MenuButton>
                  </ActionsWrap>
                )}
              </FileCardRow>
            );
          })}
        </List>
      )}
      {canAdd && (
        <FileUpload
          key={documents.map((d) => d.file.id).join(',')}
          id={id ? `${id}-upload` : 'document-upload'}
          section={DOCUMENTS_SECTION}
          value={undefined}
          onChange={handleAdd}
          disabled={disabled}
          showPreview={false}
          serverError={canAdd ? serverDocumentsError : null}
        />
      )}
    </Wrapper>
  );
}
